import { useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';

/**
 * Hook for managing user settings with Supabase
 * 
 * REQUIRED SUPABASE SETUP:
 * 
 * 1. Create a Storage Bucket named "user-uploads"
 *    - Go to Storage in Supabase Dashboard
 *    - Create new bucket: "user-uploads"
 *    - Set as Public bucket
 * 
 * 2. Configure Storage Policies for "user-uploads" bucket:
 *    
 *    Policy: "Users can upload their own avatars"
 *    Operation: INSERT
 *    Target roles: authenticated
 *    Policy definition:
 *    ```sql
 *    (bucket_id = 'user-uploads'::text) 
 *    AND (auth.uid()::text = (storage.foldername(name))[1])
 *    ```
 *    
 *    Policy: "Users can update their own avatars"
 *    Operation: UPDATE
 *    Target roles: authenticated
 *    Policy definition:
 *    ```sql
 *    (bucket_id = 'user-uploads'::text) 
 *    AND (auth.uid()::text = (storage.foldername(name))[1])
 *    ```
 *    
 *    Policy: "Anyone can view avatars"
 *    Operation: SELECT
 *    Target roles: public
 *    Policy definition:
 *    ```sql
 *    bucket_id = 'user-uploads'::text
 *    ```
 * 
 * 3. Folder structure in bucket will be: avatars/{userId}-{timestamp}.{ext}
 */

export const useUserSettings = () => {
  const { user, setUser } = useAuthStore();
  const [isUpdating, setIsUpdating] = useState(false);

  const updateProfile = async (updates: {
    displayName?: string;
    email?: string;
  }) => {
    try {
      setIsUpdating(true);

      // Update user metadata for display name
      if (updates.displayName !== undefined) {
        const { data, error } = await supabase.auth.updateUser({
          data: {
            display_name: updates.displayName,
          },
        });

        if (error) throw error;
        if (data.user) {
          setUser(data.user);
        }
      }

      // Update email if changed
      if (updates.email !== undefined && updates.email !== user?.email) {
        const { data, error } = await supabase.auth.updateUser({
          email: updates.email,
        });

        if (error) throw error;
        if (data.user) {
          setUser(data.user);
          toast.success('Verification email sent to new address');
        }
      }

      toast.success('Profile updated successfully');
      return { success: true };
    } catch (error: any) {
      toast.error(error.message || 'Failed to update profile');
      return { success: false, error: error.message };
    } finally {
      setIsUpdating(false);
    }
  };

  const updatePassword = async (currentPassword: string, newPassword: string) => {
    try {
      setIsUpdating(true);

      // First verify current password by attempting to sign in
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user?.email || '',
        password: currentPassword,
      });

      if (signInError) {
        throw new Error('Current password is incorrect');
      }

      // Update password
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      toast.success('Password changed successfully');
      return { success: true };
    } catch (error: any) {
      toast.error(error.message || 'Failed to change password');
      return { success: false, error: error.message };
    } finally {
      setIsUpdating(false);
    }
  };

  const uploadProfilePicture = async (file: File) => {
    try {
      setIsUpdating(true);

      // Validate file size (2MB max)
      if (file.size > 2 * 1024 * 1024) {
        throw new Error('File size must be less than 2MB');
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        throw new Error('File must be an image');
      }

      // Create unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `avatars/${user?.id}/${fileName}`;

      console.log('Uploading to path:', filePath);
      console.log('User ID:', user?.id);

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('user-uploads')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('user-uploads')
        .getPublicUrl(filePath);

      // Update user metadata with new avatar URL
      const { data, error } = await supabase.auth.updateUser({
        data: {
          avatar_url: urlData.publicUrl,
        },
      });

      if (error) throw error;
      if (data.user) {
        setUser(data.user);
      }

      toast.success('Profile picture updated successfully');
      return { success: true, url: urlData.publicUrl };
    } catch (error: any) {
      toast.error(error.message || 'Failed to upload profile picture');
      return { success: false, error: error.message };
    } finally {
      setIsUpdating(false);
    }
  };

  return {
    isUpdating,
    updateProfile,
    updatePassword,
    uploadProfilePicture,
  };
};
